import { IconLink, IconUserScan } from '@tabler/icons-react';
import { Button } from '@zorro/design-system/v2/Button';
import { TextInput } from '@zorro/design-system/v2/TextInput';
import { LabelWithTooltip, Space, Text } from '@zorro/web/design-system';
import { useMonolithMutation } from '@zorro/web/ui-utils';
import { Controller, UseFormReturn } from 'react-hook-form';

import { CopyToClipboard } from '../../../CopyToClipboard';
import { SendInviteFormFields } from './SendInviteUtils';

type Props = {
  sendInviteForm: UseFormReturn<SendInviteFormFields>;
  employerId: string;
};

export const InviteByLinkForm = ({ employerId, sendInviteForm }: Props) => {
  'use no memo';
  const { mutate: mutateInviteEmployer, data: inviteLink = '' } =
    useMonolithMutation({
      method: 'employersControllerInviteEmployerAdmin',
      successMessage: 'Employer invite link created',
    });

  const { control, watch, formState } = sendInviteForm;
  const { isValid } = formState;
  const email = watch('email');

  const handleSubmit = async () => {
    try {
      await mutateInviteEmployer([employerId, email, false]);
    } catch {
      /* empty */
    }
  };

  return (
    <>
      <Text fw={600}>Step 1: Create an invitation</Text>
      <Controller
        control={control}
        name="email"
        render={({ field: { ...rest } }) => (
          <TextInput
            {...rest}
            placeholder="Add invitation account email"
            rightSectionWidth="165px"
            size="lg"
            rightSectionProps={{ style: { paddingRight: '4px' } }}
            rightSection={
              <Button
                size="sm"
                variant="primary"
                disabled={!isValid}
                onClick={handleSubmit}
                leftSection={<IconUserScan />}
              >
                Create invitation
              </Button>
            }
            label={
              <LabelWithTooltip
                label="Invitation account email"
                tooltipLabel="Email of the person or team responsible for setting up the employer account"
                isRequired
              />
            }
          />
        )}
      />
      <Space />
      <Text fw={600} c={inviteLink ? 'zorroCoal.9' : 'zorroGray.6'}>
        Step 2: Share invitation link
      </Text>
      <CopyToClipboard text={inviteLink}>
        <Button
          size="md"
          variant="secondary"
          disabled={!inviteLink}
          leftSection={<IconLink />}
        >
          Copy link
        </Button>
      </CopyToClipboard>
    </>
  );
};
